/**
 * Carrying the short-lived credit token from the email link to the checkout.
 *
 * The claim endpoint exchanges the year-long token that was mailed to the
 * customer for one that lives TWO HOURS, and redirects into the shop with
 * `?credit_token=` (or `?credit_error=<reason>`). Nothing else in this stack
 * carries a short-lived value across a navigation, so this follows the shape of
 * `gclid-tracker.ts` — capture from the URL on arrival, store `{value,
 * capturedAt}`, enforce a TTL on read, self-expire — with gclid's ninety days
 * cut to two hours.
 *
 * TWO things are load-bearing here.
 *
 * 1. The token must SURVIVE a navigation. The customer lands on the homepage,
 *    builds a cart, and reaches checkout on a different route; anything held in
 *    React state is gone by then, and `useOrder`'s `refetchOnMount: "always"`
 *    means even the order is re-fetched under it.
 *
 * 2. The token must LEAVE THE URL, immediately. It authorises spending money.
 *    Left in the address bar it reaches browser history, the Referer header of
 *    every subsequent request, and any analytics that reports the page URL —
 *    the same leak that has already been fixed twice on the backend side of
 *    this feature. `credit_token` is also in SENSITIVE_QUERY_PARAMS so that the
 *    window between the load and this cleanup is covered too.
 *
 *    That window is no longer covered by luck: `credit-url-guard.ts` runs at
 *    parse time, before the Meta Pixel's PageView, and strips the parameter
 *    itself. By the time the effect below runs there is usually nothing left in
 *    the URL to find, so the token arrives through the guard's HAND-OFF
 *    instead. Both routes are read here, and the URL still wins when both hold
 *    something — a second claim link opened in the same tab must not lose to a
 *    hand-off left over from the first.
 *
 * The clock is a parameter, defaulted, so the TTL can be tested against a fixed
 * `now` the way `validate-coupon.ts` does.
 */
import {
  CreditProblem,
  creditProblemFromUrlReason,
} from "./credit-problem";
import {
  clearCreditTokenHandoff,
  readCreditTokenHandoff,
} from "./credit-url-guard";
import type { RedeemGiftCardCodeDto } from "src/services/generated-api";

/**
 * MIRRORS the backend's `EXCHANGED_CREDIT_TOKEN_TTL_SECONDS`
 * (photo-upload-backend/src/core/credit/credit-link.ts). The two must move
 * together, and this one must never be the SHORTER of the pair: whichever
 * expires first is the real lifetime, so a frontend cap below the server's
 * hides credit that is still perfectly good — `CreditSection` renders nothing
 * for a customer holding a live token. That is exactly what happened when this
 * said ten minutes and the server was raised to two hours.
 *
 * Two hours because the token is issued when the customer lands and is needed
 * at checkout, and a real photo-ordering session — picking, uploading, waiting
 * on thumbnails, choosing sizes — does not fit in ten minutes.
 *
 * Measured from CAPTURE, a moment or two AFTER the server signed it, so this
 * window closes very slightly later than the server's, never earlier. That
 * errs in the right direction: the worst case is one refused apply, which
 * `creditProblemFromApiError` turns into the same "open the link again"
 * sentence. Expiring early would instead hide credit that was still good.
 */
export const CREDIT_TOKEN_TTL_MS = 2 * 60 * 60 * 1000;

export const CREDIT_TOKEN_PARAM = "credit_token";
export const CREDIT_ERROR_PARAM = "credit_error";

const STORAGE_KEY = "credit_claim_v1";

/**
 * localStorage is an external store, and this is its subscription.
 *
 * `useOrderCredit` reads the token through `useSyncExternalStore` rather than
 * copying it into state from an effect. That removes a mount-order race — the
 * banner that STORES the token and the section that READS it are different
 * components, and effects fire children-first, so a section mounted on the
 * landing page could read before the banner had written — and it means every
 * mounted `CreditSection` (the drawer can sit over the delivery step) sees the
 * same answer the moment the token is stored or dropped.
 *
 * `storage` events only fire in OTHER tabs, so same-tab writes have to
 * announce themselves; both are wired up in `subscribeToCreditToken`.
 */
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function subscribeToCreditToken(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === STORAGE_KEY) listener();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    listeners.delete(listener);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

type StoredCreditToken = {
  value: string;
  capturedAt: number; // ms epoch
};

export function storeCreditToken(value: string, now: number = Date.now()): void {
  if (typeof window === "undefined" || !value) return;
  try {
    const payload: StoredCreditToken = { value, capturedAt: now };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    notify();
  } catch {
    // Private mode, quota, disabled storage. The customer simply does not get
    // the credit applied automatically; nothing breaks.
  }
}

/**
 * The live token, or null.
 *
 * A lapsed entry is REMOVED as it is read, so a stale token cannot sit in
 * storage being offered to the server over and over.
 */
export function readStoredCreditToken(now: number = Date.now()): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredCreditToken;
    if (!parsed?.value || typeof parsed.capturedAt !== "number") return null;
    if (now - parsed.capturedAt > CREDIT_TOKEN_TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      // Deliberately NOT notified: this runs inside a read, and a read that
      // tells React the store changed re-renders the component that is reading
      // it, forever. The expiry is observed by the next read either way.
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

/**
 * What a credit token says about itself, read WITHOUT verifying it. Only
 * ever used to choose which of two tokens to keep and how to label the
 * balance; the server verifies every token it is sent, so a forged payload
 * buys nothing here.
 *
 * `scoped`: a lot-scoped token, the kind POST /credit/redeem-code hands back
 * (`scope: "lot"`). Absent scope is the whole-address kind from /credit/claim.
 *
 * `lotIds`: the gift cards a scoped token speaks for. Redeem-code tokens now
 * carry them as a list (`lots`), because a second card entered on the same
 * cart is ADDED to the first rather than replacing it. Tokens issued before
 * that carried one `lot` (or `lotId`) string, and still read as a list of
 * one. Empty for a whole-address token.
 */
export type CreditTokenClaims = {
  scoped: boolean;
  lotIds: string[];
  email: string;
  /** Seconds since the epoch; 0 when missing. */
  exp: number;
};

function lotIdsOf(payload: Record<string, unknown>): string[] {
  if (Array.isArray(payload.lots)) {
    return payload.lots.filter((x): x is string => typeof x === "string");
  }
  const legacy = payload.lot ?? payload.lotId;
  return typeof legacy === "string" ? [legacy] : [];
}

export function decodeCreditToken(token: string): CreditTokenClaims | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      Array.from(atob(padded), (c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join(""),
    );
    const payload = JSON.parse(json) as Record<string, unknown>;
    if (!payload || typeof payload !== "object") return null;
    return {
      scoped: payload.scope === "lot",
      lotIds: lotIdsOf(payload),
      email: typeof payload.email === "string" ? payload.email.trim().toLowerCase() : "",
      exp: typeof payload.exp === "number" ? payload.exp : 0,
    };
  } catch {
    return null;
  }
}

/**
 * Who a token may be SENT for: the order on screen and the signed-in account.
 *
 * A stored token spends one address's money, and localStorage outlives both
 * a sign-out and a guest changing the order's email. So a held token is used
 * only when the address it speaks for (its `email` claim) is this order's
 * shipping email or the signed-in account's. The server refuses any other
 * use anyway (CREDIT_EMAIL_MISMATCH), but by then the token has already been
 * handed to a request made for somebody else.
 */
export type CreditTokenAudience = {
  orderEmail?: string | null;
  accountEmail?: string | null;
};

const normalizeEmail = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

export function creditTokenUsableFor(token: string, audience: CreditTokenAudience): boolean {
  const email = decodeCreditToken(token)?.email ?? "";
  if (!email) return false;
  return (
    email === normalizeEmail(audience.orderEmail) ||
    email === normalizeEmail(audience.accountEmail)
  );
}

/** The live stored token when it may be sent for `audience`, else null. */
export function readUsableCreditToken(
  audience: CreditTokenAudience,
  now: number = Date.now(),
): string | null {
  const held = readStoredCreditToken(now);
  return held && creditTokenUsableFor(held, audience) ? held : null;
}

/**
 * The body of POST /credit/redeem-code, carrying the token held now (if any,
 * and only one that may be sent for this order — `readUsableCreditToken`).
 *
 * The server builds the answer FROM that token: a held card token comes back
 * widened to this card as well (its `lots` plus this one), and a held
 * whole-address token comes back whole-address again. Without it the answer
 * would name this card alone, and a second card typed on the same cart would
 * hide the first.
 */
export function redeemCodeDto(
  giftCardCode: string,
  orderId: string,
  audience: CreditTokenAudience,
  now: number = Date.now(),
): RedeemGiftCardCodeDto {
  const held = readUsableCreditToken(audience, now);
  return held ? { giftCardCode, orderId, creditToken: held } : { giftCardCode, orderId };
}

/**
 * Stores the token a redeemed gift-card code handed back — unless a live
 * WHOLE-ADDRESS token for the same email is held. That one is kept whatever
 * the answer is: it already covers the card just typed and every other lot
 * of the address, so no answer adds scope to it. The server is meant to
 * return it unchanged, but this does not depend on that: an answer built
 * without it (a request sent before it was stored, say) would be narrower,
 * and dropping it would take the address's other lots off the next apply
 * (which releases and re-redeems).
 *
 * Anything else is replaced, because the answer adds scope: an earlier
 * card's token (the answer includes its lots too), a lapsed token, or one
 * for ANOTHER address — the card was claimed to this order's owner, and a
 * token for someone else cannot spend on this order anyway.
 *
 * KNOWN RACE, accepted: two tabs redeeming at once each send the token held
 * when they started, so the second answer to arrive lacks the first tab's
 * card and overwrites it here. It is rare (two codes typed in two tabs within
 * one round-trip) and nothing is lost: the card stays claimed to the address,
 * and typing its code again adds it back, since re-redeeming a card already
 * claimed to the same email succeeds.
 */
export function storeRedeemedCreditToken(value: string, now: number = Date.now()): void {
  const held = readStoredCreditToken(now);
  if (held && held !== value) {
    const heldClaims = decodeCreditToken(held);
    const redeemed = decodeCreditToken(value);
    const heldCoversIt =
      heldClaims !== null &&
      !heldClaims.scoped &&
      heldClaims.exp * 1000 > now &&
      heldClaims.email !== "" &&
      heldClaims.email === redeemed?.email;
    if (heldCoversIt) return;
  }
  storeCreditToken(value, now);
}

/**
 * Which kind of usable token is held: "lot" (the typed gift cards),
 * "address" (every lot of the address), or null for none — including a held
 * token that may not be sent for `audience`.
 */
export function creditTokenScope(
  audience: CreditTokenAudience,
  now: number = Date.now(),
): "lot" | "address" | null {
  const token = readUsableCreditToken(audience, now);
  if (!token) return null;
  return decodeCreditToken(token)?.scoped ? "lot" : "address";
}

/**
 * @param onlyIf clear only while THIS token is the one stored. A refusal
 *        arrives after a round-trip, and a second claim link opened meanwhile
 *        must not be thrown away for the first one's refusal.
 */
export function clearStoredCreditToken(onlyIf?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (onlyIf !== undefined && readStoredCreditToken() !== onlyIf) return;
    window.localStorage.removeItem(STORAGE_KEY);
    notify();
  } catch {
    // As above.
  }
}

/**
 * A sign-out: the held token spends the signed-out customer's money, and the
 * next person on this browser must not find it. Called from every place that
 * ends a session on purpose (the logout, the admin logout, a refresh the
 * server refused).
 *
 * There is deliberately no "has the account changed since last time?"
 * bookkeeping on top of this. Sessions start and end in too many places to
 * observe them all, and a stale record DELETES good tokens: a guest's fresh
 * claim link, opened after a session that ended silently, was dropped as if
 * it were the old account's. What keeps one customer's token out of another
 * customer's request is the email binding (`creditTokenUsableFor`), which
 * holds whether or not any clear ran.
 */
export function clearCreditTokenOnSignOut(): void {
  clearStoredCreditToken();
}

/**
 * Take the credit params out of the address bar, leaving everything else.
 *
 * Deletes only its own two params and replaces state with the CURRENT
 * `window.history.state` preserved — exactly what `clearOrderSessionFromUrl`
 * does, and for the same reason: passing `null` there breaks App Router
 * client-side navigation. Wiping the whole query string would also drop the
 * guest session and the ad attribution that the rest of the page reads.
 *
 * @returns whether anything was actually removed, so a no-op is distinguishable.
 */
export function clearCreditParamsFromUrl(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const present = [CREDIT_TOKEN_PARAM, CREDIT_ERROR_PARAM].filter((p) =>
    params.has(p),
  );
  if (present.length === 0) return false;
  for (const p of present) params.delete(p);
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    window.location.pathname + (query ? `?${query}` : ""),
  );
  return true;
}

export type CreditArrival = {
  /** A live claim token, already stored. */
  token: string | null;
  /** Why the claim failed, ready for `creditProblemMessage`. */
  problem: CreditProblem | null;
};

/**
 * Read `?credit_token=` / `?credit_error=` on arrival, persist the token, and
 * clean both off the URL.
 *
 * `searchParams.get` (as `gclid-tracker.ts` uses) rather than either of the two
 * duplicated regex `getUrlParameter` helpers: it decodes percent-encoding, and
 * the exchanged JWT is percent-encoded by the redirect.
 */
/**
 * Whether a refused claim (`?credit_error=<reason>`) ends the customer's
 * ownership of the credit, so a claim token already held must be dropped.
 *
 * Dropped: `invalid` and `missing` (the link proves nothing), `voided`
 * (support's kill switch) and `lapsed` (the credit is over).
 *
 * KEPT for `on_your_cart` (the credit is held on the claimant's own open
 * cart, and the token is their proof for it) and for `spent`: the backend sends it both for credit spent on a placed
 * order and for credit merely HELD on an open cart — the customer's own, as a
 * rule — and the code cannot tell the two apart. Dropping the token in the
 * second case strands the credit on that cart for a guest, who can then no
 * longer prove it is theirs. Kept too for reasons that say nothing about
 * ownership (`expired` email link, `unknown`, `error`) and for reasons this
 * build does not know. A token kept is still only ever sent for its own
 * address (`readUsableCreditToken`), and a dead one is dropped on its first
 * refusal by the balance or apply endpoints (`creditTokenRefused`).
 */
const OWNERSHIP_ENDING_CLAIM_REASONS = new Set(["invalid", "missing", "voided", "lapsed"]);

export function claimRefusalEndsOwnership(reason: string): boolean {
  return OWNERSHIP_ENDING_CLAIM_REASONS.has(reason);
}

export function captureCreditFromUrl(now: number = Date.now()): CreditArrival {
  if (typeof window === "undefined") return { token: null, problem: null };

  let token: string | null = null;
  let reason: string | null = null;
  try {
    const params = new URL(window.location.href).searchParams;
    token = params.get(CREDIT_TOKEN_PARAM);
    reason = params.get(CREDIT_ERROR_PARAM);
  } catch {
    return { token: null, problem: null };
  }

  // The guard took the token out of the URL before the pixel could report it,
  // and left it here. Consumed unconditionally, whether or not it is used, so
  // a stale one cannot be found on a later load of the same tab.
  const handedOff = readCreditTokenHandoff();
  if (handedOff !== null) clearCreditTokenHandoff();
  if (token === null && reason === null) token = handedOff;

  // Nothing to do with credit. Leave the URL exactly as it was.
  if (token === null && reason === null) return { token: null, problem: null };

  clearCreditParamsFromUrl();

  if (reason !== null) {
    // Only a refusal that ENDS ownership drops the token already held. A
    // `spent` claim is also what the customer gets when the credit sits on
    // their own open cart (the email link clicked a second time, ops walk B4),
    // and the held token is then their proof for that cart.
    if (claimRefusalEndsOwnership(reason)) clearStoredCreditToken();
    return { token: null, problem: creditProblemFromUrlReason(reason) };
  }

  // `?credit_token=` with nothing after it is a broken link, not a token.
  if (!token) return { token: null, problem: "invalid" };

  storeCreditToken(token, now);
  return { token, problem: null };
}
